import React, {
	ReactElement,
	useState,
	ChangeEvent,
	useEffect,
	useContext,
	useCallback,
} from "react"
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	Button,
	Box,
	Typography,
	makeStyles,
	FormControlLabel,
	Switch,
} from "@material-ui/core"
import { Machine } from "xstate"
import { useMachine } from "@xstate/react"
import Alert from "@material-ui/lab/Alert"
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline"
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline"
import HighlightOffIcon from "@material-ui/icons/HighlightOff"
import { green, orange } from "@material-ui/core/colors"
import { ImportResponse, DatabaseNames } from "../../modules/__config/shape"
import Loading from "../../components/Loading"
import Spacer from "../../components/Spacer"
import ImportClient, {
	loadBlobAsJSON,
	validateJSONData,
} from "../../modules/import-client"
import syncClientsWithDb from "../../data/client/syncWithDB"
import { ClientContext } from "../../state/client"
import { ClientDataStruct } from "../../data/_config/shape"
import ClientUseCase from "../../data/client/ClientLogic"

type DialogProps = {
	open: boolean
	onClose: () => void
}

const useStyles = makeStyles((theme) => ({
	filename: {
		marginLeft: theme.spacing(1),
	},
	contentText: {
		margin: theme.spacing(2, 0),
	},
	fileactions: {
		marginTop: theme.spacing(2),
		"& > button + button": {
			marginLeft: theme.spacing(1),
		},
	},
	result: {
		display: "flex",
		alignItems: "center",
		marginBottom: theme.spacing(1),
		"& > * + *": {
			marginLeft: theme.spacing(1),
		},
		"& > strong": {
			fontWeight: theme.typography.fontWeightMedium,
		},
	},
}))

// #region Machine Definitions
interface MachineSchema {
	states: {
		idle: {}
		loading: {}
		success: {}
		failure: {}
	}
}

type MachineEvents =
	| { type: "FETCH" }
	| { type: "RESOLVE" }
	| { type: "REJECT" }
	| { type: "RETRY" }

const importClientMachine = Machine<null, MachineSchema, MachineEvents>({
	id: "importClient",
	initial: "idle",
	states: {
		idle: {
			on: {
				FETCH: "loading",
			},
		},
		loading: {
			on: {
				RESOLVE: "success",
				REJECT: "failure",
			},
		},
		success: {
			on: {
				RETRY: "idle",
			},
		},
		failure: {
			on: {
				RETRY: {
					target: "idle",
				},
			},
		},
	},
})
// #endregion

const databaseConfig: Record<DatabaseNames, string> = {
	ClientDatabase: "Podaci o projektu / kompaniji",
	ActionChecklistDatabase: "Sifarnici",
	CFCDatabase: "Cash flow podaci",
	HealthCheckDatabase: "S krive",
}

const successContent = {
	isfalse: {
		content: "U datoteci za uvoz nisu pronađeni podaci.",
		icon: RemoveCircleOutlineIcon,
	},
	istrue: {
		content: "Podaci su uspešno učitani.",
		icon: CheckCircleOutlineIcon,
	},
	iserror: {
		content: "",
		icon: HighlightOffIcon,
	},
}

export default function ImportClientDialog({
	open,
	onClose,
}: DialogProps): ReactElement {
	const cls = useStyles()
	const [machine, send] = useMachine(importClientMachine)
	const { dispatch } = useContext(ClientContext)

	const [errorMsg, setErrorMsg] = useState("")
	const [response, setResponse] = useState<ImportResponse>([
		false,
		true,
		new Error("Pomoć"),
	])
	const [file, setFile] = useState<File>()
	const [dupClient, setDupClient] = useState<ClientDataStruct>()
	const [overwrite, setOverwrite] = useState(true)

	const checkForExistingClient = useCallback(async () => {
		if (file) {
			const json = await loadBlobAsJSON(file)

			if (json instanceof Error || !validateJSONData(json)) {
				setDupClient(undefined)
				return
			}

			if (json.ClientDatabase !== null) {
				const clientId = json.ClientDatabase.data.data?.[0].rows?.[0]?.id || -1

				if (clientId !== -1) {
					const possibleClient = await ClientUseCase.findById(clientId)
					setDupClient(possibleClient)
					return
				}
			}
		}

		setDupClient(undefined)
	}, [file])

	useEffect(() => {
		if (machine.value === "idle") {
			setFile(undefined)
			setDupClient(undefined)
		}
	}, [machine])

	useEffect(() => {
		checkForExistingClient()
	}, [file, checkForExistingClient])

	const startImporting = async (): Promise<void> => {
		if (!file) {
			setErrorMsg("Za nastavak je potrebna datoteka")
			send("REJECT")
			return
		}

		send("FETCH")
		try {
			const res = await ImportClient(file, overwrite)
			await syncClientsWithDb(dispatch)
			setResponse(res)
			send("RESOLVE")
		} catch (e) {
			setErrorMsg(e.message)
			send("REJECT")
		}
	}

	const RetryButton = (title: string): ReactElement => (
		<Button
			onClick={(): void => {
				send("RETRY")
			}}
			variant="contained"
			color="primary"
		>
			{title}
		</Button>
	)

	// Upload a file of type JSON
	const FileUpload = (): ReactElement => (
		<>
			<label htmlFor="contained-button-file">
				<input
					accept="application/json"
					style={{ display: "none" }}
					id="contained-button-file"
					type="file"
					onChange={(e: ChangeEvent<HTMLInputElement>): void => {
						setFile(e.target.files?.[0] || undefined)
					}}
				/>
				<Button variant="outlined" component="span">
					Izaberite .json file
				</Button>
			</label>
			<Typography variant="caption" className={cls.filename}>
				{file && file.name}
			</Typography>
		</>
	)

	const FileActions = (): ReactElement => (
		<Box className={cls.fileactions}>
			<Button color="primary" variant="contained" onClick={startImporting}>
				Učitajte podatke
			</Button>
			<Button onClick={(): void => setFile(undefined)}>Obrišite fajl</Button>
		</Box>
	)

	const ShowFailure = (): ReactElement => (
		<>
			<Alert severity="error">
				{errorMsg ||
					"Nešto nije u redu pri obradi zahteva. Proverite otpremljenu datoteku i pokušajte ponovo."}
			</Alert>
			<Spacer />
			{RetryButton("Pokušajte ponovo")}
		</>
	)

	const ShowSuccess = (): ReactElement => {
		const keys = Object.keys(databaseConfig) as DatabaseNames[]

		return (
			<>
				{response.map(
					(res, idx): ReactElement => {
						if (res instanceof Error) {
							return (
								<Box key={keys[idx]} className={cls.result}>
									<successContent.iserror.icon color="error" />
									<Typography component="strong">
										{databaseConfig[keys[idx]]}:
									</Typography>
									<Typography>{res.message}</Typography>
								</Box>
							)
						}

						const config = successContent[res ? "istrue" : "isfalse"]

						return (
							<Box key={keys[idx]} className={cls.result}>
								<config.icon
									style={res ? { color: green[500] } : { color: orange[500] }}
								/>
								<Typography component="strong">
									{databaseConfig[keys[idx]]}:
								</Typography>
								<Typography>{config.content}</Typography>
							</Box>
						)
					}
				)}
				<Spacer space={3} />
				{RetryButton("Import drugog fajla")}
			</>
		)
	}

	const overwriteOnChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setOverwrite(e.target.checked)
		},
		[setOverwrite]
	)

	const DuplicateOptions = (): ReactElement => {
		if (!dupClient) return <></>

		return (
			<>
				<Spacer />
				<Alert severity="warning">
					Datoteka koju ste izabrali zameniće podatke za kompaniju &quot;
					{dupClient.name}&quot;. Da li želite da nastavite sa prepisivanjem
					podataka ili želite da kreirate novu kompaniju / projekat? Ovo se ne
					može poništiti.
				</Alert>
				<FormControlLabel
					control={
						<Switch
							checked={overwrite}
							onChange={overwriteOnChange}
							name="overwrite-client-data"
							color="primary"
						/>
					}
					label="Pregazite podatke?"
				/>
			</>
		)
	}

	const DialogBody = (): ReactElement => {
		switch (machine.value) {
			case "failure":
				// Consistently show error message to the user
				return <ShowFailure />
			case "success":
				// Show status of each of the database items
				return <ShowSuccess />
			case "loading":
				// Show loader while importing
				return <Loading />
			case "idle":
			default:
				return (
					<>
						<FileUpload />
						{DuplicateOptions()}
						{file && <FileActions />}
					</>
				)
		}
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Import podataka</DialogTitle>
			<DialogContent>
				<Typography component="p" variant="body1" className={cls.contentText}>
					<strong>Izjava o odricanju odgovornosti</strong>
				</Typography>
				<Typography component="p" variant="body1" className={cls.contentText}>
					Ako ste prethodno izvezli svoj rad / podatke, moći ćete da uvezete te
					podatke sa uređaja i nastavite tamo gde ste stali.
				</Typography>
				<Typography component="p" variant="body1" className={cls.contentText}>
					Podaci koje unesete u komplet čuvaće se samo na ovom uređaju.
					Brisanjem keš memorije pregledača izbrisaće se svi podaci klijenta.
				</Typography>
			</DialogContent>
			<DialogContent>
				<DialogBody />
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Zatvori</Button>
			</DialogActions>
		</Dialog>
	)
}
